import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useWebSocket, CONNECTION_STATES } from "../contexts/WebSocketContext";
import { useAuth } from "../components/AuthProvider";
import "./AgentChat.css";
import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import DOMPurify from "dompurify";
import EmailApprovalUI from "./EmailApprovalUI";
import AgentAvatar from "./AgentAvatar";
import AppMessagingService from "../services/AppMessagingService";
import MentionDropdown from "./MentionDropdown";
import COOFloatingButton from "./COOFloatingButton";
import ToolCallIndicator from "./ToolCallIndicator";
import getCaretCoordinates from "textarea-caret-position"; // Import AppMessagingService
import { getHumanName, processMessageContent } from "../config/agentIdentities"; // Add import

export const AgentChat = ({
  campaignActive,
  campaignState,
  onCampaignStateChange,
  dashboardConnectionState,
  agents = [], // Ensure agents prop is properly defined
  campaignMode = 'supervised', // Receive from Dashboard
  onToggleActivityPanel, // New prop for toggling the activity panel
  showActivityPanel = false, // New prop to show panel state
  hideInput = false, // New prop to hide input area
  animateFromButton = false, // New prop to trigger book-opening animation from COO button
  // COO Integration Props
  showCOOButton = false,
  expandMode = false,
  onCOOMessage,
  onCOOFileUpload,
  onCOOAssistantMessage,
}) => {
  // Debug log to check if campaignMode is being received
  console.log('[AgentChat] Campaign mode:', campaignMode);

  // This will be calculated later in the component
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [feedbackMode, setFeedbackMode] = useState(false);
  const [isRoomJoined, setIsRoomJoined] = useState(false);
  const [currentEmail, setCurrentEmail] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [mentionState, setMentionState] = useState({
    isActive: false,
    query: "",
    position: { top: 0, left: 0 },
  });
  const [isAgentWorking, setIsAgentWorking] = useState(false);
  const textareaRef = useRef(null);
  const didSyncOnReload = useRef(false); // Add a flag to prevent state overwrite

  // Stable, deduplicated agents list (supports string or object items)
  const uniqueAgents = useMemo(() => {
    if (!agents || !Array.isArray(agents)) return [];
    const acc = {};
    for (const a of agents) {
      if (!a) continue;
      if (typeof a === 'string') {
        const id = a;
        acc[id] = {
          ...(acc[id] || {}),
          id,
          name: a,
          status: acc[id]?.status || 'active',
        };
      } else {
        const id = a.id || a.name;
        if (!id) continue;
        acc[id] = {
          ...(acc[id] || {}),
          ...a,
          id,
          name: a.name || id,
          status: a.status || acc[id]?.status || 'active',
        };
      }
    }
    return Object.values(acc);
  }, [agents]);

  // Set up effect to handle feedback mode based on email review requests
  useEffect(() => {
    // If there's a current email and an email review request for it, enable feedback mode
    if (currentEmail && messages.some(msg => msg.type === "email_review_request" && msg.emailData?.id === currentEmail.id)) {
      setFeedbackMode(true);
    }
  }, [currentEmail, messages]);

  // Track approval state so Dashboard can stay in sync
  const approvalStateRef = useRef({ emailId: null, feedbackMode: false });
  const prevCampaignActiveRef = useRef(campaignActive);
  useEffect(() => {
    const nextState = {
      emailId: currentEmail?.id || null,
      feedbackMode,
    };
    const prevState = approvalStateRef.current;
    const campaignChanged = prevCampaignActiveRef.current !== campaignActive;
    const approvalStateChanged =
      prevState.emailId !== nextState.emailId ||
      prevState.feedbackMode !== nextState.feedbackMode;

    if ((approvalStateChanged || campaignChanged) && onCampaignStateChange) {
      approvalStateRef.current = nextState;
      prevCampaignActiveRef.current = campaignActive;
      onCampaignStateChange(campaignActive, {
        feedbackMode,
        currentEmail: nextState.emailId,
      });
    }
  }, [campaignActive, currentEmail, feedbackMode, onCampaignStateChange]);

  // Auto-focus textarea when feedback mode is activated
  useEffect(() => {
    if (feedbackMode && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [feedbackMode]);

  // Email approval handlers
  const handleEmailApprove = useCallback(async (email) => {
    if (!token) {
      throw new Error("No authentication token available");
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/emails/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          credentials: "include",
          mode: "cors",
          body: JSON.stringify({
            id: email.id,
            approved: true,
            feedback: "",
            recipient_email: email.recipient_email || email.to,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error approving email:", error);
      throw error;
    }
  }, [token]);

  const handleEmailReject = useCallback(async (email, feedback) => {
    if (!token) {
      throw new Error("No authentication token available");
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/emails/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          credentials: "include",
          mode: "cors",
          body: JSON.stringify({
            id: email.id,
            approved: false,
            feedback: feedback || "",
            recipient_email: email.recipient_email || email.to,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Unknown error occurred" }));
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error rejecting email:", error);
      throw error;
    }
  }, [token]);
  const messagesEndRef = useRef(null);
  const messageBuffer = useRef([]);
  const processingBuffer = useRef(false);
  const agentChatContainerRef = useRef(null);
  const { emit, on, off } = useWebSocket();
  // Use dashboard connection state if provided, otherwise use local state
  const isConnected = dashboardConnectionState === CONNECTION_STATES.CONNECTED;
  const currentCampaignId = useRef(null);

  // Removed excessive render logging to prevent memory overflow

  //console.log("AgentChat render:", {
  //  isConnected,
  //  campaignActive,
  //  messagesCount: messages.length,
  //  agentsCount: agents.length,
  //  userEmail: user?.email,
  //});

  // Debug logging - commented out to prevent memory overflow
  // useEffect(() => {
  //   console.log("AgentChat state changed:", {
  //     isConnected,
  //     isRoomJoined,
  //     messagesCount: messages.length,
  //     agentsCount: agents.length,
  //     userEmail: user?.email,
  //   });
  // }, [isConnected, isRoomJoined, messages.length, agents.length, user?.email]);

  // Update campaign state when it changes
  useEffect(() => {
    if (!campaignState || !onCampaignStateChange) return;

    // Don't validate if we're initializing
    if (campaignState.isInitializing) {
      return;
    }

    // Validate campaign state format
    const isValidCampaignState =
      campaignState.status &&
      ["active", "stopped", "paused"].includes(campaignState.status);

    // Reset to default stopped state if invalid or failed
    if (!isValidCampaignState) {
      console.warn("Invalid campaign state format:", campaignState);

      // Clear current campaign ID if this was our active campaign
      if (campaignState.campaign_id === currentCampaignId.current) {
        window.currentCampaignId = null;
        currentCampaignId.current = null;
        console.log("Cleared currentCampaignId due to invalid state");
      }

      return;
    }

    // Update campaign ID if provided and not already synced on reload
    if (campaignState.campaign_id && !didSyncOnReload.current) {
      window.currentCampaignId = campaignState.campaign_id;
      currentCampaignId.current = campaignState.campaign_id;
    }
  }, [campaignState, onCampaignStateChange]);

  // Clear campaign ID and state when component unmounts
  useEffect(() => {
    return () => {
      window.currentCampaignId = null;
      currentCampaignId.current = null;
      onCampaignStateChange?.({
        status: "stopped",
        agents: [],
        timestamp: new Date().toISOString(),
        error: null,
        success: false,
        campaign_id: null,
      });
    };
  }, [onCampaignStateChange]);

  // Clear campaign ID when component unmounts
  useEffect(() => {
    return () => {
      window.currentCampaignId = null;
    };
  }, []);

  // Clear messages when campaign changes
  useEffect(() => {
    if (
      campaignState?.campaign_id &&
      campaignState.campaign_id !== currentCampaignId.current
    ) {
      setMessages([]);
      messageBuffer.current = [];
    }
  }, [campaignState?.campaign_id]);

  // Message buffer processor with campaign ID filtering and deduplication
  // Store processed message signatures to prevent duplicates
  const processedMessages = useRef(new Set());

  // Create refs for values used in processMessageBuffer to keep it stable
  const messagesRef = useRef(messages);
  const campaignStateRef = useRef(campaignState);
  const onCampaignStateChangeRef = useRef(onCampaignStateChange);
  const handleEmailApproveRef = useRef(handleEmailApprove);
  const handleEmailRejectRef = useRef(handleEmailReject);


  // Update refs when values change
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    approvalStateRef.current = {
      ...approvalStateRef.current,
      emailId: currentEmail?.id || null,
    };
  }, [currentEmail]);

  useEffect(() => {
    campaignStateRef.current = campaignState;
  }, [campaignState]);

  useEffect(() => {
    onCampaignStateChangeRef.current = onCampaignStateChange;
  }, [onCampaignStateChange]);

  useEffect(() => {
    handleEmailApproveRef.current = handleEmailApprove;
  }, [handleEmailApprove]);

  useEffect(() => {
    handleEmailRejectRef.current = handleEmailReject;
  }, [handleEmailReject]);

  const processMessageBuffer = useCallback(() => {
    // console.log(`[AgentChat] processMessageBuffer called. Buffer length: ${messageBuffer.current.length}, currentCampaignId: ${currentCampaignId.current}`);

    // If we don't have a campaign ID but there are messages with campaign IDs in the buffer,
    // sync to the first one we find (this handles reload scenarios)
    if (!currentCampaignId.current && messageBuffer.current.length > 0) {
      // console.log("[AgentChat] No campaign ID set, checking buffer for messages with campaign IDs...");
      const messageWithCampaignId = messageBuffer.current.find(msg => msg.campaign_id);
      if (messageWithCampaignId?.campaign_id) {
        // console.log(`[AgentChat] Auto-syncing campaign ID to: ${messageWithCampaignId.campaign_id}`);
        currentCampaignId.current = messageWithCampaignId.campaign_id;
        didSyncOnReload.current = true;
      } else {
        // console.log("[AgentChat] No messages with campaign IDs found in buffer. Pausing processing.");
        // console.log("[AgentChat] Buffer contents:", messageBuffer.current.map(msg => ({ type: msg.type, agent_id: msg.agent_id, campaign_id: msg.campaign_id })));
        return;
      }
    }

    if (messageBuffer.current.length > 0 && !processingBuffer.current) {
      processingBuffer.current = true;
      const message = messageBuffer.current.shift();

      // Add debug logging for message structure - commented out to prevent memory overflow
      // console.log(
      //   "[AgentChat] Raw message structure:",
      //   JSON.stringify(message, null, 2)
      // );

      // Skip if message is null or undefined
      if (!message) {
        processingBuffer.current = false;
        return;
      }

      // console.log("[AgentChat] Processing message:", {
      //   messageType: message?.type || "undefined",
      //   eventName: message?.eventName || message?.event_name || "no_event",
      //   campaignId: message?.campaign_id || null,
      //   timestamp: message?.timestamp || new Date().toISOString(),
      //   hasEmailData: Boolean(message?.emailData),
      //   messageSource: message?.messageSource || message?.source || "unknown",
      //   messageId: message?.id || message?.emailData?.id,
      // });

      // Early duplicate check based on message ID
      const messageId = message?.id || message?.emailData?.id;
      if (messageId && messagesRef.current.some((m) => m.id === messageId)) {
        // console.log("Skipping duplicate message by ID:", messageId);
        processingBuffer.current = false;
        return;
      }

      // Additional deduplication: check if same agent sent identical content recently
      const agentId = message?.agent_id;
      const contentStr = typeof message.content === 'string' ? message.content : JSON.stringify(message.content);
      if (agentId && contentStr && messagesRef.current.some((m) => 
        m.agent_id === agentId && 
        (m.content === contentStr || m.content === contentStr.substring(0, 100))
      )) {
        processingBuffer.current = false;
        return;
      }

      // Create a content signature for additional deduplication
      const contentSignature =
        message.campaign_id && message.content
          ? `${message.campaign_id}_${typeof message.content === "string" ? message.content : JSON.stringify(message.content)}`
          : null;

      // Check if message was already processed by content signature
      if (contentSignature && processedMessages.current.has(contentSignature)) {
        // console.log(
        //   "Skipping duplicate message by content signature:",
        //   contentSignature
        // );
        processingBuffer.current = false;
        return;
      }

      // Mark as processed if we have a content signature
      if (contentSignature) {
        processedMessages.current.add(contentSignature);
      }

      setMessages((prevMessages) => {
        const currentCampaignIdValue = currentCampaignId.current;

        // Keep only last 100 messages to prevent memory overflow
        const trimmedMessages = prevMessages.length > 100 ? prevMessages.slice(-100) : prevMessages;

        // Handle email review requests from both UnifiedMessaging and AppMessagingService
        if (
          message.type === "email_review_request" ||
          message.event_name === "email_review_request" ||
          (message.content &&
            message.content.type === "email_review_request") ||
          (message.data && message.data.type === "email_review_request")
        ) {
          // console.log("[AgentChat] Processing email review request:", {
          //   emailId: message.emailData?.id || message.id,
          //   subject: message.emailData?.subject || message.subject,
          //   timestamp: message.timestamp,
          //   source: message.source || "event",
          // });

          // Normalize email data structure from both UnifiedMessaging and AppMessagingService
          const emailData = {
            id:
              message.emailData?.id ||
              message.data?.id ||
              message.id ||
              `email_${Date.now()}`,
            subject:
              message.emailData?.subject ||
              message.data?.subject ||
              message.subject ||
              message.content?.subject,
            body:
              message.emailData?.body ||
              message.data?.body ||
              message.body ||
              message.content?.body ||
              (typeof message.content === "object"
                ? message.content.content
                : message.content),
            recipient_email:
              message.emailData?.recipient_email ||
              message.data?.recipient_email ||
              message.recipient_email ||
              message.content?.recipient_email,
            signature:
              message.emailData?.signature ||
              message.data?.signature ||
              message.signature ||
              message.content?.signature ||
              "",
            is_html:
              message.emailData?.is_html ||
              message.data?.is_html ||
              message.is_html ||
              message.content?.is_html ||
              (typeof (message.emailData?.body ?? message.data?.body ?? message.body ?? message.content?.body) === 'string' && /<\w|<!DOCTYPE html/i.test((message.emailData?.body ?? message.data?.body ?? message.body ?? message.content?.body))) || false,
            campaign_id:
              message.campaign_id ||
              message.data?.campaign_id ||
              message.emailData?.campaign_id ||
              message.content?.campaign_id ||
              campaignStateRef.current?.campaign_id,
            source:
              message.source ||
              message.data?.source ||
              message.messageSource ||
              "event",
          };

          // Check for duplicate email reviews
          const isDuplicateReview = prevMessages.some(
            (msg) =>
              msg.type === "email_review_request" &&
              msg.emailData?.id === emailData.id
          );

          if (isDuplicateReview) {
            // console.log("Skipping duplicate email review:", emailData);
            return prevMessages;
          }

          // IMPORTANT: Only set currentEmail if we don't already have an email with this ID being displayed
          // This prevents duplicate rendering of the EmailApprovalUI component
          const isAlreadyDisplayed =
            approvalStateRef.current.emailId === emailData.id;
          if (!isAlreadyDisplayed) {
            setCurrentEmail(emailData);
          } else {
            // console.log(
            //   "Email already being displayed, not updating currentEmail state:",
            //   emailData.id
            // );
          }

          // Create normalized message with email data
          const normalizedMessage = {
            ...message,
            id: emailData.id,
            type: "email_review_request",
            emailData,
            content: `📧 Email Review Request (via ${emailData.source})\nTo: ${emailData.recipient_email}\nSubject: ${emailData.subject}\n\n${emailData.body}`,
            timestamp: message.timestamp || Date.now(),
            campaign_id: emailData.campaign_id,
            source: emailData.source,
          };

          // We'll only add the EmailApprovalUI component if we're not already displaying it via currentEmail
          // This prevents duplicate rendering of the EmailApprovalUI component
          const messageWithUI = {
            ...normalizedMessage,
            content: isAlreadyDisplayed ? (
              normalizedMessage.content
            ) : (
              <EmailApprovalUI
                email={emailData}
                onApprove={() => handleEmailApproveRef.current(emailData)}
                onReject={(feedback) => handleEmailRejectRef.current(emailData, feedback)}
              />
            ),
          };
          return [...trimmedMessages, messageWithUI];
        }

        // For non-email review messages, check campaign ID (but skip this check during reload sync)
        if (
          currentCampaignIdValue &&
          message.campaign_id &&
          message.campaign_id !== currentCampaignIdValue &&
          !didSyncOnReload.current
        ) {
          // console.log("Skipping message from different campaign:", {
          //   reason: "campaign mismatch",
          //   messageCampaignId: message.campaign_id,
          //   currentCampaignId: currentCampaignIdValue,
          // });
          return prevMessages;
        }

        // If this is a reload trigger, sync campaign ID from buffered messages
        if (message.trigger_load) {
          // Look through the entire buffer to find the first message with a campaign_id
          const messageWithCampaignId = messageBuffer.current.find(msg => msg.campaign_id);
          if (messageWithCampaignId?.campaign_id) {
            // console.log(`[AgentChat] Syncing campaign ID on reload to: ${messageWithCampaignId.campaign_id}`);
            currentCampaignId.current = messageWithCampaignId.campaign_id;
            didSyncOnReload.current = true; // Set the flag to prevent overwrite
          }
        }

        const isDuplicate = prevMessages.some(
          (msg) =>
            msg.agent_id === message.agent_id &&
            msg.content === message.content &&
            msg.timestamp === message.timestamp
        );

        if (isDuplicate) {
          // console.log("Duplicate message detected, skipping:", message);
          return prevMessages;
        }

        // Handle Firebase server timestamp
        let timestamp;
        if (
          message.timestamp &&
          typeof message.timestamp === "object" &&
          message.timestamp.sv === "timestamp"
        ) {
          timestamp = message._timestamp || new Date().toISOString();
        } else if (typeof message.timestamp === "number") {
          timestamp = new Date(message.timestamp).toISOString();
        } else {
          timestamp = message.timestamp || new Date().toISOString();
        }

        // Campaign completion should only be triggered by backend verification
        // not by message content parsing
        // Removed faulty text-based completion detection

        const normalizedMessage = {
          ...message,
          content: typeof message.content === 'object' && message.content !== null ? (message.content.content || message.content.text || JSON.stringify(message.content)) : message.content,
          timestamp,
          type: message.type || "system",
          campaign_id: message.campaign_id || campaignStateRef.current?.campaign_id,
          eventName: message.eventName || message.event_name || "no_event",
          messageSource: message.messageSource || message.source || "unknown",
          tool_calls: message.tool_calls || null,
        };

        // Simpler message addition - grouping is now handled in rendering
        requestAnimationFrame(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        });

        return [...trimmedMessages, normalizedMessage];
      });

      setTimeout(() => {
        processingBuffer.current = false;
        if (messageBuffer.current.length > 0) {
          processMessageBuffer();
        }
      }, 5);
    }
  }, []); // Remove dependencies to prevent recreation

  const joinRoom = useCallback(async () => {
    if (!isConnected || !user?.email || isRoomJoined) {
      console.warn("Cannot join room:", {
        isConnected,
        hasEmail: !!user?.email,
        isRoomJoined,
      });
      return;
    }

    try {
      // Wait for AppMessagingService to be ready
      if (!AppMessagingService.messaging) {
        console.log("Waiting for AppMessagingService to be ready...");
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Encode email to match backend format (samuel@grupa.ai -> samuel@grupa_ai)
      const encodedEmail = user.email.includes('@') ?
        (() => {
          const [localPart, domain] = user.email.split('@');
          const encodedDomain = domain.replace(/\./g, '_');
          return `${localPart}@${encodedDomain}`;
        })() : user.email;

      // Join room through both Socket.IO and Firebase
      const success = await emit("join", {
        room: encodedEmail,
        email: user.email,
        metadata: {
          timestamp: Date.now(),
          clientInfo: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
          },
        },
      });

      // Also join through AppMessagingService for Firebase with encoded email
      const firebaseSuccess = await AppMessagingService.joinRoom(encodedEmail);
      // console.log("Room join results:", {
      //   socketSuccess: success,
      //   firebaseSuccess,
      // });

      if (success && firebaseSuccess) {
        setIsRoomJoined(true);
        // console.log(
        //   "Successfully joined room through both Socket.IO and Firebase"
        // );
      }
    } catch (error) {
      console.error("Failed to join room:", error);
    }
  }, [isConnected, user?.email, isRoomJoined, emit]);

  // Single useffect for room joining
  useEffect(() => {
    if (isConnected && !isRoomJoined && user?.email) {
      // console.log("Connection established, attempting to join room");
      joinRoom();
    }
  }, [isConnected, isRoomJoined, user?.email, joinRoom]);

  useEffect(() => {
    // Remove the isConnected check to ensure listeners are set up immediately
    // This allows Firebase events to be caught even before Socket.IO connects
    // console.log("Setting up message listeners:", { isConnected });

    const handleError = (error) => {
      console.error("Socket error:", error);
      setFeedback(`Error: ${error.message}`);
    };

    // Set up message event listeners with deduplication
    const handleGroupChatMessage = (message) => {
      // console.log("Received message:", {
      //   type: message?.type,
      //   id: message?.id,
      //   campaign_id: message?.campaign_id,
      //   content: message?.content,
      //   emailData: message?.emailData,
      //   event_name: message?.event_name,
      // });

      // Skip if message is already in buffer or messages
      const isDuplicate = messageBuffer.current.some(
        (m) =>
          m.id === message.id ||
          (m.type === message.type &&
            m.campaign_id === message.campaign_id &&
            m.content === message.content)
      );

      if (isDuplicate) {
        // console.log("Skipping duplicate message:", {
        //   type: message.type,
        //   id: message.id,
        //   campaign_id: message.campaign_id,
        // });
        return;
      }

      messageBuffer.current.push(message);
      processMessageBuffer();
    };

    // Use the unified messaging on() which handles both Firebase and Socket
    on("group_chat_message", handleGroupChatMessage);

    on("email_review_request", (message) => {
      // console.log("Received email review request directly:", message);
      // Skip if this is an event or if the message is already in the buffer
      if (
        message.eventId ||
        messageBuffer.current.some(
          (m) =>
            m.id === message.id ||
            (m.emailData && m.emailData.id === message.emailData?.id)
        )
      ) {
        // console.log("Skipping duplicate email review request:", {
        //   eventId: message.eventId,
        //   id: message.id,
        //   emailId: message.emailData?.id,
        // });
        return;
      }

      if ((new Date(message.timestamp || 0)).getTime() < (window.sessionStartTime ?? Date.now())) return;

      messageBuffer.current.push({
        ...message,
        type: "email_review_request",
        timestamp: message.timestamp || Date.now(),
      });
      processMessageBuffer();
    });

    // Listen for mode changes (if backend emits them for multi-tab sync)
    // Currently commented out in backend, but keeping listener for future use
    // on("mode_changed", (data) => {
    //   console.log("Campaign mode changed:", data);
    //   setCampaignMode(data.mode);
    // });

    on("error", handleError);

    return () => {
      off("group_chat_message");
      off("email_review_request");
      // off("mode_changed");
      off("error");
    };
  }, [processMessageBuffer, on, off]);  // Need on, off in dependencies for proper listener setup

  // Removed duplicate joinRoom effects to prevent repeated joins/re-renders

  // Handle disconnection
  useEffect(() => {
    if (!isConnected && isRoomJoined) {
      // console.log("Lost connection, marking room as unjoined");
      setIsRoomJoined(false);
    }
  }, [isConnected, isRoomJoined]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsRoomJoined(false);
    };
  }, []);

  // Single effect for scrolling
  useEffect(() => {
    if (messagesEndRef.current) {
      console.log("Messages updated, scrolling to bottom:", {
        messageCount: messages.length,
        lastMessage: messages[messages.length - 1],
      });
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, messages.length]);

  useEffect(() => {
    if (messageBuffer.current.length > 0) {
      processMessageBuffer();
    }
  }, [processMessageBuffer]);

  // Explicitly trigger buffer processing when campaign context is ready
  useEffect(() => {
    if (campaignState?.campaign_id && currentCampaignId.current && messageBuffer.current.length > 0) {
      console.log("[AgentChat] Campaign context is ready, processing buffered messages.");
      processMessageBuffer();
    }
  }, [campaignState, processMessageBuffer]);

  useEffect(() => {
    if (isConnected && campaignActive) {
      onCampaignStateChange(campaignActive, { agentCount: agents.length });
    }
  }, [agents.length, isConnected, campaignActive, onCampaignStateChange]);

  const sendMessage = (messageContent) => {
    if (isConnected) {
      emit("message", {
        token: user.token,
        room: user.email,
        message: messageContent,
      });
    }
  };

  const handleEmailApproval = async (approved, feedbackText, email) => {
    if (approved) {
      try {
        // Make HTTP call to approve
        await handleEmailApprove(email);

        // Send chat message
        sendMessage({
          role: "user",
          content: "Email approved for sending.",
          metadata: {
            type: "email_approval",
            approved: true,
          },
        });

        // Update the email review message to mark it as approved
        setMessages((prevMessages) => {
          return prevMessages.map((msg) => {
            if (
              msg.type === "email_review_request" &&
              msg.emailData?.id === email.id
            ) {
              return { ...msg, approved: true };
            }
            return msg;
          });
        });

        // Clear the current email state
        setCurrentEmail(null);
      } catch (error) {
        console.error("Error approving email:", error);
        sendMessage({
          role: "system",
          content: `Error: Failed to approve email - ${error.message}`,
          metadata: { type: "error" },
        });
      }
    } else if (feedbackText === "FEEDBACK_MODE") {
      // Store email for when feedback is submitted
      setCurrentEmail(email);
      setFeedbackMode(true);

      // Show feedback prompt
      sendMessage({
        role: "system",
        content:
          "Please provide your feedback about what changes you would like to make to the email.",
        metadata: {
          type: "email_feedback_request",
        },
      });
    } else {
      try {
        // Make HTTP call to reject with feedback
        await handleEmailReject(email, feedbackText);

        // Send chat message
        sendMessage({
          role: "user",
          content: `Email rejected with feedback: ${feedbackText}`,
          metadata: {
            type: "email_approval",
            approved: false,
            feedback: feedbackText,
          },
        });

        // Update the email review message to mark it as rejected
        setMessages((prevMessages) => {
          return prevMessages.map((msg) => {
            if (
              msg.type === "email_review_request" &&
              msg.emailData?.id === email.id
            ) {
              return { ...msg, approved: false, feedback: feedbackText };
            }
            return msg;
          });
        });

        // Clear the current email state
        setCurrentEmail(null);
      } catch (error) {
        console.error("Error rejecting email:", error);
        sendMessage({
          role: "system",
          content: `Error: Failed to reject email - ${error.message}`,
          metadata: { type: "error" },
        });
      }
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    // Add user's message to chat
    const userMessage = {
      agent_id: "user",
      content: feedback,
      timestamp: new Date().toISOString(),
      type: "message",
    };
    setMessages((prev) => [...prev, userMessage]);

    if (feedbackMode && currentEmail) {
      try {
        // Make HTTP call to reject with feedback
        await handleEmailReject(currentEmail, feedback);

        // Send the feedback message
        sendMessage({
          role: "user",
          content: feedback,
          metadata: {
            type: "email_approval",
            approved: false,
            feedback: feedback,
          },
        });

        // Update the email review message to mark it as rejected
        setMessages((prevMessages) => {
          return prevMessages.map((msg) => {
            if (
              msg.type === "email_review_request" &&
              msg.emailData?.id === currentEmail.id
            ) {
              return { ...msg, approved: false, feedback: feedback };
            }
            return msg;
          });
        });

        // Clear state
        setFeedbackMode(false);
        setCurrentEmail(null);
      } catch (error) {
        console.error("Error rejecting email:", error);
        sendMessage({
          role: "system",
          content: `Error: Failed to reject email - ${error.message}`,
          metadata: { type: "error" },
        });
      }
    } else {
      // Normal chat message
      sendMessage(feedback);
    }

    setFeedback("");
  };

  const getAgentInitials = (name) => {
    console.log("Getting initials for name:", name);
    if (!name) return "";
    if (name === "system") return "🤖";

    // First try to get capitals
    const capitals = name.match(/[A-Z][a-z]+/g);
    if (capitals && capitals.length >= 1) {
      console.log("Found capital words:", capitals);
      return capitals
        .slice(0, 2)
        .map((word) => word[0])
        .join("");
    }

    // If no capitals, split by underscore or space
    const words = name.split(/[_ ]+/);
    console.log("Split into words:", words);
    return words
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  const getMessageContent = (msg) => {
    if (!msg || !msg.content) return "";

    if (typeof msg.content === "string") {
      return msg.content;
    }

    if (typeof msg.content === "object") {
      return (
        msg.content.text || msg.content.message || msg.content.content || ""
      );
    }

    return "";
  };

  const getMessagePosition = (message, index, messages) => {
    // Check for user messages - these should always be on the right
    if (
      message.role === "user" ||
      message.agent_id === "user" ||
      message.agent_id === user?.email ||
      message.sender === user?.email
    ) {
      return "right";
    }

    // System messages always on the left
    if (message.agent_id === "system") return "left";

    // Default to left if first message
    if (index === 0) return "left";

    const prevMessage = messages[index - 1];
    if (!prevMessage) return "left";

    // Check for @ mentions in content
    let contentToCheck = "";

    // Safely get current message content
    if (message.content) {
      if (typeof message.content === "string") {
        contentToCheck = message.content;
      } else if (typeof message.content === "object") {
        contentToCheck =
          message.content.text ||
          message.content.message ||
          message.content.content ||
          "";
      }
    }

    // Extract mentioned agent from @ mention
    const mentionMatch = contentToCheck.match(/@([A-Za-z][A-Za-z_]*)/);
    const mentionedAgent = mentionMatch ? mentionMatch[1] : null;

    // Check if the mentioned agent exists in the agents list
    const isValidMention =
      mentionedAgent &&
      agents && agents.length > 0 &&
      agents.some((agent) => {
        if (!agent) return false;  // Skip undefined/null agents
        if (typeof agent === "object") {
          return (
            (agent.name && typeof agent.name === 'string' && agent.name.toLowerCase() === mentionedAgent.toLowerCase()) ||
            (agent.id && typeof agent.id === 'string' && agent.id.toLowerCase() === mentionedAgent.toLowerCase())
          );
        }
        return typeof agent === 'string' && agent.toLowerCase() === mentionedAgent.toLowerCase();
      });

    // If it's a reply to the previous message (either through @ mention or recipient_id)
    if (message.agent_id === prevMessage.agent_id) {
      // Same agent, keep the thread on same side
      return prevMessage.position;
    }

    // Check for direct reply through @ mention
    if (isValidMention) {
      const prevAgentId =
        typeof prevMessage.agent_id === "object"
          ? prevMessage.agent_id.id || prevMessage.agent_id.name
          : prevMessage.agent_id;

      if (prevAgentId && typeof prevAgentId === 'string' && prevAgentId.toLowerCase() === mentionedAgent.toLowerCase()) {
        // Direct reply to previous message, alternate side
        return prevMessage.position === "left" ? "right" : "left";
      }
    }

    // Check for reply through recipient_id
    if (message.recipient_id) {
      const prevAgentId =
        typeof prevMessage.agent_id === "object"
          ? prevMessage.agent_id.id || prevMessage.agent_id.name
          : prevMessage.agent_id;

      if (prevAgentId && message.recipient_id && prevAgentId === message.recipient_id) {
        // Direct reply to previous agent, alternate side
        return prevMessage.position === "left" ? "right" : "left";
      }
    }

    // Start new conversation thread on opposite side
    return prevMessage.position === "left" ? "right" : "left";
  };

  const renderAgentStatus = () => {
    if (!uniqueAgents || uniqueAgents.length === 0) return null;

    return (
      <div className="agent-avatars">
        {uniqueAgents.map((agent) => (
          <AgentAvatar
            key={agent.name?.toLowerCase().includes('digitaltwin')
              ? `${agent.id || agent.name}-${window.currentProspectName}`
              : (agent.id || agent.name)}
            agent={agent}
            getAgentInitials={getAgentInitials}
          />
        ))}
      </div>
    );
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";

    const messageDate = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - messageDate) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    // For messages less than 1 minute old
    if (diffInMinutes < 1) {
      return "Now";
    }

    // For messages less than 1 hour old
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }

    // For messages from today
    if (diffInHours < 24 && messageDate.getDate() === now.getDate()) {
      return messageDate.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }

    // For messages from yesterday
    if (diffInDays === 1) {
      return "Yesterday";
    }

    // For older messages
    return messageDate.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatMessage = (msg) => {
    // Ensure we have a string before processing
    const content = getMessageContent(msg);

    // First process the content through our identity manager
    let processedContent = processMessageContent(content);

    // Then handle additional formatting
    processedContent = processedContent
      // Preserve line breaks
      .replace(/\n/g, "  \n")
      // Convert URLs to markdown links
      .replace(
        /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g,
        (url) => `[${url}](${url})`
      )
      // Convert emails to markdown links
      .replace(
        /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
        (email) => `[${email}](mailto:${email})`
      );

    // Sanitize the content
    processedContent = DOMPurify.sanitize(processedContent);

    return (
      <div className="message-text">
        <ReactMarkdown
          components={{
            p: ({ children }) => (
              <span
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  display: "block",
                }}
              >
                {children}
              </span>
            ),
            text: ({ children }) => {
              // Process mentions in text nodes
              if (typeof children === "string") {
                // Use the comprehensive agent name pattern
                const parts = children.split(
                  /(@(?:Website_Analyzer_Agent|Sales_Assistant_Agent|EmailReviewerAgent|AuditOutgoingEmailAgent|EmailSenderAgent|EmailResponseGeneratorAgent|EngagementAnalysisAgent|FollowUpStrategizerAgent|EmailResponderAgent))\b/g
                );
                return parts.map((part, index) => {
                  if (part.startsWith("@")) {
                    const agentName = part.substring(1);
                    return (
                      <span key={index} className="agent-mention">
                        @{getHumanName(agentName)}
                      </span>
                    );
                  }
                  return part;
                });
              }
              return children;
            },
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </div>
    );
  };

  const renderMessage = (msg, index) => {
    //console.log("Rendering message:", msg);
    //console.log("Message type:", msg.type);
    //console.log("Message metadata:", msg.metadata);

    // Get position before rendering
    const position = getMessagePosition(msg, index, messages);

    // Check if this is a reply
    const isReply =
      index > 0 &&
      ((msg.content &&
        ((typeof msg.content === "string" &&
          msg.content.match(/@([A-Za-z][A-Za-z_]*)/)) ||
          (typeof msg.content === "object" &&
            msg.content.text &&
            msg.content.text.match(/@([A-Za-z][A-Za-z_]*)/)))) ||
        (msg.recipient_id &&
          messages[index - 1].agent_id === msg.recipient_id));

    //console.log("Message type:", msg.type);
    //console.log("Message metadata:", msg.metadata);

    if (
      msg.type === "PENDING_APPROVAL" ||
      msg.type === "email_review_request"
    ) {
      //console.log("Found email review request message:", msg);

      // For email_review_request type, use the message data directly
      const emailData =
        msg.type === "email_review_request"
          ? {
            id: msg.emailData?.id || msg.id,
            subject: msg.emailData?.subject || msg.subject,
            body: msg.emailData?.body || msg.body,
            recipient_email:
              msg.emailData?.recipient_email || msg.recipient_email,
            signature: msg.emailData?.signature || msg.signature || "",
            is_html: msg.emailData?.is_html || msg.is_html,
          }
          : msg.metadata?.email;

      //console.log("Rendering EmailApprovalUI with email:", emailData);

      // Check if this email has already been processed
      if (msg.approved === true) {
        return (
          <div style={{
            background: '#F0FDF4',
            border: '1px solid #A7F3D0',
            borderLeft: '4px solid #34D399',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '8px',
            opacity: 0.7,
          }}>
            <div style={{ marginBottom: '12px', color: '#6B7280', fontSize: '12px' }}>
              <div>To: {emailData.recipient_email}</div>
              <div>Subject: {emailData.subject}</div>
            </div>
            {emailData.is_html ? (
              <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#374151', whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: emailData.body.replace(/&lt;/g, '<').replace(/&gt;/g, '>') }} />
            ) : (
              <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#374151', whiteSpace: 'pre-wrap' }}>{emailData.body}</div>
            )}
          </div>
        );
      }

      if (msg.approved === false) {
        return (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderLeft: '4px solid #F87171',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '8px',
            opacity: 0.7,
          }}>
            <div style={{ marginBottom: '12px', color: '#6B7280', fontSize: '12px' }}>
              <div>To: {emailData.recipient_email}</div>
              <div>Subject: {emailData.subject}</div>
            </div>
            {emailData.is_html ? (
              <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#374151', whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: emailData.body.replace(/&lt;/g, '<').replace(/&gt;/g, '>') }} />
            ) : (
              <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#374151', whiteSpace: 'pre-wrap' }}>{emailData.body}</div>
            )}
          </div>
        );
      }

      return (
        <EmailApprovalUI
          email={emailData}
          onApprove={() => handleEmailApproval(true, null, emailData)}
          onReject={() =>
            handleEmailApproval(false, "FEEDBACK_MODE", emailData)
          }
        />
      );
    }

    if (msg.type === "TIMER_STARTED") {
      return (
        <div className="timer-message">
          <div className="timer-header">
            ⏳ Auto-sending in {msg.metadata.timing.wait_time}s
          </div>
          <EmailApprovalUI
            email={msg.metadata.email}
            onApprove={() =>
              handleEmailApproval(true, null, msg.metadata.email)
            }
            onReject={() =>
              handleEmailApproval(false, "FEEDBACK_MODE", msg.metadata.email)
            }
            mode="timer"
            waitTime={msg.metadata.timing.wait_time}
          />
        </div>
      );
    }

    if (msg.type === "AUTONOMOUS_SEND") {
      return (
        <div className="autonomous-message">
          <div className="autonomous-header">
            🚀 Sending email automatically
          </div>
          <div className="recall-info">
            You have {msg.metadata.timing.recall_window}s to recall this email
          </div>
          <EmailApprovalUI
            email={msg.metadata.email}
            mode="autonomous"
            recallWindow={msg.metadata.timing.recall_window}
            onRecall={() =>
              handleEmailApproval(
                false,
                "Email recalled by user",
                msg.metadata.email
              )
            }
          />
        </div>
      );
    }

    const isEmailMessage = msg.type === "PENDING_APPROVAL" || msg.type === "email_review_request";
    const emailStatusClass = isEmailMessage && msg.approved === true ? "email-approved" :
      isEmailMessage && msg.approved === false ? "email-rejected" : "";

    // Hide indexed messages that have no content (they're grouped into parent)
    if (msg.is_indexed && !msg.content && !msg.tool_calls) {
      return null;
    }

    return (
      <motion.div
        key={msg.id || `${msg.agent_id}-${index}-${msg.timestamp}`}
        className={`message ${msg.type === "agent_message" ? "agent" : "system"} ${position} ${isReply ? "reply" : ""} ${emailStatusClass} ${msg.is_indexed ? "indexed-message" : ""}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        {/* Always show avatar on the correct side */}
        {position === "left" && (
          <div
            className="message-avatar-container"
            style={{ width: "32px", height: "32px", flexShrink: 0 }}
          >
            <AgentAvatar
              agent={msg.agent_id || msg.agentId || (msg.type === 'system' ? 'system' : null)}
              getAgentInitials={getAgentInitials}
              style={{ width: "32px", height: "32px", fontSize: "12px" }}
            />
          </div>
        )}

        <div className={`message-content ${position}`}>
          <div className="message-sender">
            {position === "right"
              ? user?.displayName || user?.name || user?.email?.split("@")[0]
              : getHumanName(msg.agent_name || msg.agent_id)}
          </div>
          <div className="message-body">
            {formatMessage(msg)}
            {/* Tool Call Indicator - Hide for parent messages that have grouped children (shown in group card instead) */}
            {msg.tool_calls && msg.tool_calls.length > 1 && !msg.isParent && (
              <>
                {console.log('[TOOL CALL - RENDERING] ToolCallIndicator rendering for message:', {
                  agentId: msg.agent_id,
                  toolCallsCount: msg.tool_calls.length,
                  shouldShow: msg.tool_calls.length > 1
                })}
                <ToolCallIndicator
                  toolCalls={msg.tool_calls}
                />
              </>
            )}
          </div>
          <div className="message-time">{formatMessageTime(msg.timestamp)}</div>
        </div>

        {position === "right" && (
          <div
            className="message-avatar-container"
            style={{ width: "32px", height: "32px", flexShrink: 0 }}
          >
            <img
              src={
                user?.picture ||
                user?.photoURL ||
                user?.avatar ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.email || "")}&background=2563EB&color=fff`
              }
              alt={user?.displayName || user?.email?.split("@")[0]}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "2px solid #fff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.email || "")}&background=2563EB&color=fff`;
              }}
            />
          </div>
        )}
      </motion.div>
    );
  };

  useEffect(() => {
    console.log("AgentChat state updated:", {
      messagesCount: messages.length,
      lastMessage: messages[messages.length - 1],
      campaignActive,
      isConnected,
    });
  }, [messages, campaignActive, isConnected]);

  // Monitor agent working state
  useEffect(() => {
    // Agent is working if campaign is active and has agents
    const isWorking = campaignActive &&
      campaignState?.status === 'active' &&
      agents &&
      agents.length > 0;
    setIsAgentWorking(isWorking);
  }, [campaignActive, campaignState?.status, agents]);

  // Handle campaign events
  useEffect(() => {
    if (!isConnected) return;

    const handleCampaignStarted = (data) => {
      console.log("Campaign started:", data);
      onCampaignStateChange(true, {
        startTime: new Date().toISOString(),
        ...data.metrics,
      });
    };

    const handleCampaignError = (error) => {
      if (error?.sortTime && error.sortTime < (window.sessionStartTime || Date.now())) return;
      console.error("Campaign error:", error);
      onCampaignStateChange(false, {
        error: error.message || "Campaign error occurred",
      });
    };

    const handleMetricsUpdate = (message) => {
      console.log('[AgentChat] Received metrics_update:', message);
      // Handle both direct metrics and wrapped format
      const metrics = message?.content?.metrics || message;
      onCampaignStateChange(campaignActive, {
        ...metrics,
        agentCount: agents.length,
      });
    };

    const handleCampaignCompleted = (data) => {
      console.log("Campaign completed event received:", data);

      // Check staleness - only process events from the last 30 seconds
      const eventTime = data?.sortTime || (data?.timestamp && typeof data.timestamp !== 'object' ? new Date(data.timestamp).getTime() : Date.now());
      const isRecent = (Date.now() - eventTime) < 30000; // 30 seconds

      if (!isRecent) {
        console.log("Ignoring stale campaign_completed event", {
          eventTime,
          currentTime: Date.now(),
          ageInSeconds: Math.round((Date.now() - eventTime) / 1000)
        });
        return;
      }

      console.log("Processing recent campaign_completed event");
      onCampaignStateChange(false, {
        completed: true,
        endTime: new Date().toISOString(),
        message: data.message || 'Campaign successfully completed'
      });
    };

    on("campaign_started", handleCampaignStarted);
    on("campaign_error", handleCampaignError);
    on("metrics_update", handleMetricsUpdate);
    on("campaign_completed", handleCampaignCompleted);
    // Auto-approve pending emails in UI when email_sent event fires (for autonomous mode)
    on("email_sent", () => {
      setMessages(msgs => msgs.map(m => m.type === "email_review_request" && !m.approved ? { ...m, approved: true } : m));
      // Clear feedback mode and return to watching agents work
      setFeedbackMode(false);
      setFeedback(""); // Clear the feedback text
      setCurrentEmail(null);
      setIsAgentWorking(true); // Show "agents are working" mode
    });
    // Auto-reject pending emails in UI when email_rejected event fires (for autonomous mode AI rejection)
    on("email_rejected", (data) => {
      setMessages(msgs => msgs.map(m => m.type === "email_review_request" && m.emailData?.id === data.email_id ? { ...m, approved: false, feedback: data.feedback } : m));
      // Clear feedback mode and return to watching agents work
      setFeedbackMode(false);
      setFeedback(""); // Clear the feedback text
      setCurrentEmail(prev => {
        // Clear if this was the current email being reviewed
        if (prev && data.email_id === prev.id) {
          return null;
        }
        return prev;
      });
      setIsAgentWorking(true); // Show "agents are working" mode
    });

    return () => {
      off("campaign_started", handleCampaignStarted);
      off("campaign_error", handleCampaignError);
      off("metrics_update", handleMetricsUpdate);
      off("campaign_completed", handleCampaignCompleted);
      off("email_sent");
      off("email_rejected");
    };
  }, [isConnected, campaignActive, onCampaignStateChange, agents.length, on, off]);

  useEffect(() => {
    console.log("Connection status:", {
      isRoomJoined,
      isConnected,
      messageCount: messages.length,
      userEmail: user?.email,
    });
  }, [isRoomJoined, isConnected, messages.length, user?.email]);

  useEffect(() => {
    if (isConnected) {
      on("connect", () => {
        console.log("Connected to chat server");
      });

      on("disconnect", () => {
        console.log("Disconnected from chat server");
        // Attempt reconnection
        setTimeout(() => {
          if (!isConnected) {
            console.log("Attempting to reconnect...");
          }
        }, 5000);
      });

      on("reconnect", () => {
        console.log("Reconnected to chat server");
      });

      return () => {
        off("connect");
        off("disconnect");
        off("reconnect");
      };
    }
  }, [isConnected]);

  const renderEmailApprovalUI = () => {
    if (currentEmail) {
      // Check if this email is already displayed in a message
      const isAlreadyInMessages = messages.some(
        (msg) =>
          msg.type === "email_review_request" &&
          msg.emailData?.id === currentEmail.id
      );

      if (isAlreadyInMessages) {
        console.log(
          "Email already displayed in messages, not rendering duplicate UI:",
          currentEmail.id
        );
        return null;
      }

      return (
        <EmailApprovalUI
          email={currentEmail}
          onApprove={(email) => handleEmailApproval(true, null, email)}
          onReject={(email, feedback) =>
            handleEmailApproval(false, feedback, email)
          }
          onRequestFeedback={(email) =>
            handleEmailApproval(false, "FEEDBACK_MODE", email)
          }
        />
      );
    }
    return null;
  };

  return (
    <div
      ref={agentChatContainerRef}
      className={`agent-chat-container ${campaignMode === 'autonomous' ? 'autonomous-mode' : ''}`}
    >
      {campaignState?.error && (
        <div className="error-banner">
          <p>{campaignState.error}</p>
          <button
            onClick={() =>
              onCampaignStateChange(campaignActive, { error: null })
            }
            className="dismiss-button"
          >
            ×
          </button>
        </div>
      )}

      <div className="chat-header">
        <button
          className={`activity-toggle-btn ${showActivityPanel ? 'active' : ''}`}
          onClick={onToggleActivityPanel}
          aria-label="Toggle Activity Panel"
          title={showActivityPanel ? "Close Activity Panel" : "Open Activity Panel"}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            {showActivityPanel ? (
              // Door open icon
              <path d="M5 3H15C15.55 3 16 3.45 16 4V16C16 16.55 15.55 17 15 17H5C4.45 17 4 16.55 4 16V4C4 3.45 4.45 3 5 3ZM7 10C6.45 10 6 10.45 6 11C6 11.55 6.45 12 7 12C7.55 12 8 11.55 8 11C8 10.45 7.55 10 7 10Z"
                stroke="currentColor" strokeWidth="1.5" fill="currentColor" />
            ) : (
              // Window icon
              <>
                <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1.5" />
                <line x1="10" y1="3" x2="10" y2="17" stroke="currentColor" strokeWidth="1.5" />
              </>
            )}
          </svg>
        </button>
        <div className="header-content">
          <div className="header-title">
            <h3>Sales Team Chat</h3>
            <span className="subtitle">
              Your Autonomous Sales Organization...
            </span>
          </div>
        </div>
        {renderAgentStatus()}
      </div>

      <div className="messages-container" style={{ minHeight: "300px" }}>
        {currentEmail && renderEmailApprovalUI()}
        {messages.length === 0 ? (
          <div className="no-messages">
            {campaignActive ? "Waiting for messages..." : "No messages yet"}{" "}
            (Total: {messages.length})
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {(() => {
              // GROUPING LOGIC: Collect messages into groups before rendering
              const groups = [];
              let currentGroup = null;

              messages.forEach((msg, index) => {
                const messageTime = msg.timestamp ? new Date(msg.timestamp).getTime() : 0;
                const sessionStart = window.sessionStartTime || Date.now();
                const showDivider = index > 0 && messageTime >= sessionStart &&
                  messages[index - 1] && new Date(messages[index - 1].timestamp).getTime() < sessionStart;

                // Check if this message should be grouped
                let shouldGroup = false;

                if (index > 0) {
                  const prevMsg = messages[index - 1];

                  if (String(prevMsg.agent_id) === String(msg.agent_id)) {
                    if (prevMsg.tool_calls && prevMsg.tool_calls.length > 0) {
                      if (msg.tool_calls && msg.tool_calls.length > 0) {
                        const prevToolIds = prevMsg.tool_calls.map(tc => tc.id).sort().join(',');
                        const currToolIds = msg.tool_calls.map(tc => tc.id).sort().join(',');

                        if (prevToolIds === currToolIds) {
                          shouldGroup = true;
                        }
                      } else {
                        shouldGroup = true;
                      }
                    }
                  }

                  if (!shouldGroup && prevMsg.is_indexed) {
                    shouldGroup = true;
                  }
                }

                if (shouldGroup && currentGroup) {
                  // Add to current group
                  currentGroup.children.push({ ...msg, showDivider });
                } else {
                  // Start a new group
                  if (currentGroup) {
                    groups.push(currentGroup);
                  }
                  currentGroup = {
                    parent: { ...msg, showDivider },
                    children: [],
                    toolCalls: msg.tool_calls || null
                  };
                }
              });

              // Don't forget the last group
              if (currentGroup) {
                groups.push(currentGroup);
              }

              console.log('[GROUPING] Generated', groups.length, 'groups');

              // Render the groups
              return groups.map((group, groupIndex) => {
                const hasChildren = group.children.length > 0;
                const hasToolCalls = group.toolCalls && group.toolCalls.length > 0;

                return (
                  <div key={groupIndex} className="message-group">
                    {/* Session divider */}
                    {group.parent.showDivider && (
                      <div style={{ textAlign: 'center', margin: '20px 0', opacity: 0.5 }}>
                        <hr style={{ border: 'none', borderTop: '1px dashed #ccc' }} />
                        <small>New Session</small>
                      </div>
                    )}

                    {/* Render parent message */}
                    {renderMessage(
                      { ...group.parent, isParent: hasChildren && hasToolCalls },
                      groupIndex,
                      messages
                    )}

                    {/* Render grouped children in card if they exist */}
                    {hasChildren && hasToolCalls && (() => {
                      const groupKey = `${group.parent.agent_id}-${groupIndex}`;
                      const isExpanded = expandedGroups.has(groupKey);

                      const toggleGroup = () => {
                        setExpandedGroups(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(groupKey)) {
                            newSet.delete(groupKey);
                          } else {
                            newSet.add(groupKey);
                          }
                          return newSet;
                        });
                      };

                      return (
                        <div className={`message-group-card ${!isExpanded ? 'collapsed' : ''}`}>
                          <button
                            className="message-group-header"
                            onClick={toggleGroup}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleGroup();
                              }
                            }}
                            type="button"
                            aria-expanded={isExpanded}
                            aria-label={`Toggle tool group - ${group.children.length} messages`}
                          >
                            <div className="message-group-header-left">
                              <span className="message-group-header-icon">▼</span>
                              <span className="message-group-header-text">
                                Using {group.toolCalls.length} tool{group.toolCalls.length > 1 ? 's' : ''}
                              </span>
                            </div>
                            <span className="message-group-header-count">
                              {group.children.length}
                            </span>
                          </button>
                          <ul className="message-group-list">
                            {group.children.map((child, childIndex) => (
                              <li key={childIndex} className="message-group-item">
                                {typeof child.content === 'string' ? child.content : JSON.stringify(child.content)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                );
              });
            })()}
          </AnimatePresence>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat input area */}

      {!hideInput && (
        <form
          onSubmit={handleFeedbackSubmit}
          className={`feedback-input-container ${animateFromButton ? 'animate-from-button' : ''}`}
        >
          <MentionDropdown
            agents={agents}
            query={mentionState.query}
            position={mentionState.position}
            visible={mentionState.isActive}
            onSelect={(agent) => {
              const textArea = document.querySelector(".feedback-input");
              const cursorPosition = textArea.selectionStart;
              const textBeforeMention = feedback
                .substring(0, cursorPosition)
                .replace(/@[^\s]*$/, "");
              const textAfterMention = feedback.substring(cursorPosition);
              const newText = `${textBeforeMention}@${agent.name} ${textAfterMention}`;
              setFeedback(newText);
              setMentionState((prev) => ({ ...prev, isActive: false }));
              textArea.focus();
            }}
          />
          <textarea
            ref={textareaRef}
            className="feedback-input"
            value={feedback}
            placeholder={
              currentEmail
                ? messages.some(msg => msg.type === "email_review_request" && msg.emailData?.id === currentEmail.id)
                  ? "Type your feedback for the email..."
                  : "Type your message to the AI team..."
                : isAgentWorking
                  ? "Your agents are working while you watch"
                  : "Type your message to the AI team..."
            }
            disabled={(!currentEmail && isAgentWorking) || (!currentEmail && !feedbackMode)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleFeedbackSubmit(e);
              }
            }}
            onChange={(e) => {
              setFeedback(e.target.value);
              const textArea = e.target;
              const cursorPosition = textArea.selectionStart;
              const textBeforeCursor = e.target.value.substring(
                0,
                cursorPosition
              );
              const mentionMatch = textBeforeCursor.match(/@([^\s]*)$/);

              if (mentionMatch && agents && agents.length > 0) {
                try {
                  const rect = textArea.getBoundingClientRect();
                  const caretCoords = getCaretCoordinates(
                    textArea,
                    cursorPosition
                  );
                  const scrollTop = textArea.scrollTop;

                  // Calculate position relative to the viewport
                  const top =
                    rect.top + window.scrollY + caretCoords.top - scrollTop;
                  const left = rect.left + window.scrollX + caretCoords.left;

                  setMentionState({
                    isActive: true,
                    query: mentionMatch[1],
                    position: {
                      top: top + 24, // Add offset to show below the caret
                      left: left,
                    },
                  });
                } catch (error) {
                  console.error(
                    "Error calculating mention dropdown position:",
                    error
                  );
                  setMentionState((prev) => ({ ...prev, isActive: false }));
                }
              } else {
                setMentionState((prev) => ({ ...prev, isActive: false }));
              }
            }}
          />
          <button
            type="submit"
            className="send-button"
            disabled={(!currentEmail && isAgentWorking) || (!currentEmail && !feedbackMode)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18l9-2zm0 0v-8"
              />
            </svg>
            {feedbackMode ? "Send Feedback" : "Send"}
          </button>
        </form>
      )}

      {/* COO Floating Button - positioned relative to this AgentChat container */}
      <COOFloatingButton
        isVisible={showCOOButton}
        campaignMode={campaignMode}
        expandMode={expandMode}
        agentChatRef={agentChatContainerRef}
        onSendMessage={onCOOMessage}
        onFileUpload={onCOOFileUpload}
        onAssistantMessage={onCOOAssistantMessage}
      />
    </div>
  );
};

AgentChat.propTypes = {
  agents: PropTypes.array,
  campaignActive: PropTypes.bool,
  campaignState: PropTypes.shape({
    isInitializing: PropTypes.bool,
    isStarting: PropTypes.bool,
    error: PropTypes.string,
    metrics: PropTypes.object,
    startTime: PropTypes.string,
  }),
  onCampaignStateChange: PropTypes.func,
  dashboardConnectionState: PropTypes.string, // Add this prop type
};

export default AgentChat;
